import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { NeonRapidsInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * NEON RAPIDS — fuel-gauge river shooter cartridge.
 *
 * Fly a ship UP a winding neon canyon (the river scrolls down to you).
 * Steer left/right between lethal canyon walls, fire forward to blast
 * patrol boats, helicopters, and jets. The signature mechanic is FUEL:
 * the tank drains constantly, so the run is a race against empty. Fly
 * OVER a green depot to refill — but shoot one and you destroy it (and
 * its fuel): points vs. gas, the classic tradeoff. Bridges span the
 * river as score gates AND respawn checkpoints. 3 lives; a crash (wall,
 * enemy, or dry tank) costs one and drops you back at the last bridge
 * with a partial tank. Lose all three → game over.
 *
 * Endless + ramps up: faster scroll, denser/faster enemies, tighter
 * channel with distance. Score = kills + distance + bridge bonuses.
 *
 * Resolution: 480×270 pixelated, landscape. Palette: a neon river-canyon
 * at night — cyan ship, magenta banks, and the fuel gauge glowing
 * green → amber → red as the tank runs dry.
 */
const module_: GameModule = {
  id: 'neon-rapids',
  title: 'NEON RAPIDS',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'shooter',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'LEFT / RIGHT', action: 'STEER ACROSS THE RIVER' },
    { keys: 'Z / SPACE', action: 'FIRE' },
    { keys: 'Z', action: 'START / RESTART' },
  ],
  touch: {
    fireA: 'FIRE',
    fireB: 'FIRE',
  },

  async preload(ctx: PreloadContext): Promise<void> {
    // Shoot — short bright pew on each shot.
    ctx.registerSfx('shoot', {
      wave: 'square',
      pitch: [
        { t: 0, f: 880 },
        { t: 12, f: 520 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 2, g: 0.12 },
        { t: 12, g: 0 },
      ],
    });
    // Boom — enemy/depot destroyed: noise crunch.
    ctx.registerSfx('boom', {
      wave: 'noise',
      pitch: [
        { t: 0, f: 320 },
        { t: 50, f: 110 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 3, g: 0.3 },
        { t: 50, g: 0 },
      ],
    });
    // Bridge — a bright confirming chord as you pass a checkpoint.
    ctx.registerSfx('bridge', {
      wave: 'triangle',
      pitch: [
        { t: 0, f: 523 },
        { t: 40, f: 784 },
        { t: 80, f: 1046 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 4, g: 0.24 },
        { t: 80, g: 0 },
      ],
    });
    // Crash — the ship going down: descending wail + sub thump.
    ctx.registerSfx('crash', {
      wave: 'sawtooth',
      pitch: [
        { t: 0, f: 440 },
        { t: 70, f: 90 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 4, g: 0.4 },
        { t: 70, g: 0 },
      ],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new NeonRapidsInstance(ctx);
  },
};

export default module_;
